import React, { useState, useEffect } from 'react'
import { Card, CardBody, Button, Spinner, Row, Col, Label } from 'reactstrap'
import { Plus, Edit2, Trash2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'

import { TableComponent as DataTable } from 'components/TableComponent'
import PMBPagination from 'components/PMBPagination'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBTooltip from 'components/PMBTooltip'
import BreadCrumbs from 'components/breadcrumbs'
import PMBSelect from 'components/PMBSelect'
import { formatDatePMB } from 'utils/formatDate'
import { URL_SETTINGS } from 'utils/pathUrl'

import useGetCommodityQuery from 'hooks/useGetCommodityQuery'
import useAutoFetchPublicVersion from 'hooks/useAutoFetchPublicVersion'

import {
  useFetchPriceTablesQuery,
  useDeletePriceTableMutation,
} from './priceTableApiSlice'
import { setFetchOption } from './priceTableSlice'
import ModalAddEditPrice from './ModalAddEditPrice'

const PriceTable = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((state) => state.priceTable)
  const autoFetchPublicVersion = useAutoFetchPublicVersion()

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isOpenModalAddEdit, setIsOpenModalAddEdit] = useState(false)
  const [dataItemById, setDataItemById] = useState(null)

  const { data: itemsGroupedData, isLoading: isLoadingItems } = useGetCommodityQuery()

  const itemOptions = React.useMemo(() => {
    if (!itemsGroupedData) return []
    return itemsGroupedData.map(group => ({
      ...group,
      options: group.options.map(opt => ({
        ...opt,
        value: opt.id
      }))
    }))
  }, [itemsGroupedData])

  const flatOptions = React.useMemo(() => itemOptions.flatMap(g => g.options || [g]), [itemOptions])

  // Add empty option for clearing
  const filterItemOptions = [{ label: 'Semua Barang', value: '' }, ...itemOptions]
  const filterUnitOptions = [{ label: 'Semua Satuan', value: '' }, ...(autoFetchPublicVersion?.unitArray || [])]

  const [deletePriceTable, { isLoading: isDeleting }] =
    useDeletePriceTableMutation()

  const { data, isLoading, refetch, isFetching } = useFetchPriceTablesQuery(
    {
      page: tag?.page ?? 0,
      size: tag?.size ?? 20,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
      itemId: tag?.itemId || undefined,
      unit: tag?.unit || undefined,
    },
    { refetchOnMountOrArgChange: true }
  )


  const handleDelete = async (id) => {
    try {
      await deletePriceTable(id).unwrap()
      enqueueSnackbar('Data berhasil dihapus', { variant: 'success' })
      setIsOpenDeleteModal(false)
      refetch()
    } catch (err) {
      enqueueSnackbar(err?.data?.message || 'Gagal menghapus data', {
        variant: 'error',
      })
    }
  }

  const columns = [
    {
      name: <span>Tanggal Dibuat</span>,
      selector: 'createdAt',
      sortable: true,
      sortField: 'createdAt',
      grow: 1,
      cell: (row) => (
        <span data-testid={`price-table-row-${row?.id}`} className="m-0">
          {formatDatePMB(row?.createdAt, row?.createdAt)}
        </span>
      ),
    },
    {
      name: <span>Nama Barang</span>,
      selector: 'item.nameItem',
      sortable: true,
      sortField: 'label',
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2 py-1">
          {row?.item?.linkFoto ? (
            <img
              src={row.item.linkFoto}
              alt={row.item.nameItem}
              width={40}
              height={40}
              className="rounded"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <div
              className="bg-light rounded d-flex align-items-center justify-content-center"
              style={{ width: 40, height: 40 }}
            >
              📦
            </div>
          )}
          <span className="m-0">{row?.item?.nameItem || '-'}</span>
        </div>
      ),
    },
    {
      name: <span>Satuan</span>,
      selector: 'unit',
      sortable: false,
      grow: 1,
      cell: (row) => <span>{row?.unit || '-'}</span>,
    },
    {
      name: <span>Harga</span>,
      selector: 'price',
      sortable: false,
      grow: 1,
      cell: (row) => (
        <span>
          Rp {new Intl.NumberFormat('id-ID').format(row?.price || 0)}
        </span>
      ),
    },
    {
      name: <span>Aksi</span>,
      center: true,
      grow: 1,
      cell: (row) => {
        return (
          <div className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
            <div id={`edit-price-${row?.id}`}>
              <Button
                color="flat-warning"
                size="sm"
                data-testid={`price-table-edit-button-${row?.id}`}
                onClick={() => {
                  setDataItemById(row)
                  setIsOpenModalAddEdit(true)
                }}
                className="btn-icon p-1"
              >
                <Edit2 size={16} />
              </Button>
            </div>
            <PMBTooltip target={`edit-price-${row?.id}`} text="Ubah" placement="top" />

            <div id={`remove-price-${row?.id}`}>
              <Button
                color="flat-danger"
                size="sm"
                data-testid={`price-table-delete-button-${row?.id}`}
                onClick={() => {
                  setDataItemById(row)
                  setIsOpenDeleteModal(false)
                  setTimeout(() => setIsOpenDeleteModal(true), 100)
                }}
                className="btn-icon p-1"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <PMBTooltip target={`remove-price-${row?.id}`} text="Hapus" placement="top" />
          </div>
        )
      },
    },
  ]

  return (
    <div className="w-100 px-3 pb-4 pt-3">
      <div className="mb-3">
        <BreadCrumbs
          breadCrumbActive="Tabel Harga"
          breadCrumbParent="Pengaturan"
          breadCrumbParentLink={URL_SETTINGS}
        />
      </div>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4 pb-2">
            <h4 className="m-0 fw-semibold">Tabel Harga</h4>
            <Button
              color="primary"
              data-testid="price-table-add-button"
              onClick={() => {
                setDataItemById(null)
                setIsOpenModalAddEdit(true)
              }}
              className="d-flex align-items-center gap-1"
            >
              <Plus size={16} /> Tambah Harga
            </Button>
          </div>

          <Row className="mb-4">
            <Col md={4} sm={6} xs={12} className="mb-3 mb-md-0">
              <Label className="fw-semibold">Barang</Label>
              <PMBSelect
                testId="price-table-filter-commodity-select"
                placeholder="Semua Barang"
                options={filterItemOptions}
                value={
                  tag?.itemId
                    ? flatOptions.find((opt) => opt.value === tag.itemId)
                    : filterItemOptions[0]
                }
                isLoading={isLoadingItems}
                onChange={(opt) => {
                  dispatch(
                    setFetchOption({
                      itemId: opt?.value || '',
                      page: 0, // Reset to first page on filter change
                    })
                  )
                }}
              />
            </Col>
            <Col md={4} sm={6} xs={12}>
              <Label className="fw-semibold">Satuan</Label>
              <PMBSelect
                testId="price-table-filter-unit-select"
                placeholder="Semua Satuan"
                options={filterUnitOptions}
                value={
                  tag?.unit
                    ? filterUnitOptions.find((opt) => opt.value === tag.unit)
                    : filterUnitOptions[0]
                }
                onChange={(opt) => {
                  dispatch(
                    setFetchOption({
                      unit: opt?.value || '',
                      page: 0, // Reset to first page on filter change
                    })
                  )
                }}
              />
            </Col>
          </Row>

          <div className="border rounded">
            <DataTable
              columns={columns}
              data={data?.items || []}
              progressPending={isLoading || isFetching}
              progressComponent={
                <div className="d-flex justify-content-center p-4">
                  <Spinner color="primary" />
                </div>
              }
              sortServer
              onSort={(column, sortDirection) => {
                dispatch(
                  setFetchOption({
                    sortby: column.sortField,
                    sortdir: sortDirection.toUpperCase(),
                  })
                )
              }}
            />
            {data?.items?.length > 0 && (
              <div className="p-3 border-top">
                <PMBPagination
                  rowCount={data?.totalItems || 0}
                  currentPage={tag?.page}
                  rowsPerPage={tag?.size}
                  setCurrentPage={(e) => {
                    dispatch(setFetchOption({ page: e }))
                  }}
                  setRowsPerPage={(e) => {
                    dispatch(setFetchOption({ size: e }))
                  }}
                />
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal(false)}
        title="Hapus Data Harga"
        desc={`Apakah Anda yakin ingin menghapus harga untuk barang ${dataItemById?.item?.nameItem || ''}?`}
        dataId={dataItemById?.id}
        onRemoveData={handleDelete}
        isLoading={isDeleting}
      />

      {isOpenModalAddEdit && (
        <ModalAddEditPrice
          isOpen={isOpenModalAddEdit}
          setIsOpen={setIsOpenModalAddEdit}
          data={dataItemById}
          refetch={refetch}
        />
      )}
    </div>
  )
}

export default PriceTable
